import React, { useState } from "react";
import { Upload, Info, Phone, Facebook, Mail } from "lucide-react";

/*
  Shared, deliberately non-functional project-inquiry UI for construction concept
  sites. Visitors can fill it out to see what the real site could support, but
  nothing is persisted or uploaded anywhere — these are unaffiliated concept demos
  and must never collect real project/contact/budget data on a business's behalf.
  Theming (colors/labels/contact) is passed in per business so each site keeps its
  own visual identity.
*/
export default function ConstructionInquiryForm({
  testidPrefix,
  accent,
  accentText = "#FFFFFF",
  panelBg = "#FFFFFF",
  border = "#E5E5E5",
  muted = "#6B6B6B",
  heading = "Start Your Project",
  subheading = "Tell us a bit about what you're planning. Our team will follow up to schedule a site visit or consultation.",
  projectTypes = ["Residential — New Build", "Residential — Renovation", "Commercial", "Institutional / Government", "Other"],
  budgetRanges = ["Under ₱1,000,000", "₱1,000,000 – ₱3,000,000", "₱3,000,000 – ₱10,000,000", "Above ₱10,000,000", "Not sure yet"],
  phone,
  phoneDisplay,
  messengerUrl,
  email,
}) {
  const [form, setForm] = useState({ name: "", contact: "", location: "", projectType: "", area: "", startDate: "", budget: "", details: "" });
  const [fileNames, setFileNames] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = (e) => {
    const names = Array.from(e.target.files || []).map((f) => f.name);
    setFileNames(names);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div data-testid={`${testidPrefix}-inquiry-notice`} className="rounded-xl p-8 text-center" style={{ background: panelBg, border: `1px solid ${border}` }}>
        <Info size={36} style={{ color: accent }} className="mx-auto" />
        <p className="mt-4 text-base font-semibold" style={{ color: "#1A1A1A" }}>{tagalog ? "Konseptong preview lamang — hindi pa konektado ang inquiry form na ito." : "Concept preview only — this inquiry form is not currently connected."}</p>
        <p role="status" aria-live="polite" className="mt-2 text-sm" style={{ color: muted }}>{tagalog ? "Direktang makipag-ugnayan gamit ang nakumpirmang contact details sa ibaba at babalikan kayo ng team." : "Please reach out directly using the verified contact details below and the team will get back to you."}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {phone && (
            <a href={`tel:${phone}`} data-testid={`${testidPrefix}-inquiry-call`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold" style={{ background: accent, color: accentText }}>
              <Phone size={16} /> {phoneDisplay || phone}
            </a>
          )}
          {messengerUrl && (
            <a href={messengerUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testidPrefix}-inquiry-messenger`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold border" style={{ borderColor: border, color: "#1A1A1A" }}>
              <Facebook size={16} /> {tagalog ? "Mag-message sa Facebook" : "Message on Facebook"}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} data-testid={`${testidPrefix}-inquiry-email`} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold border" style={{ borderColor: border, color: "#1A1A1A" }}>
              <Mail size={16} /> {email}
            </a>
          )}
        </div>
        <button onClick={() => setSubmitted(false)} data-testid={`${testidPrefix}-inquiry-reset`} className="mt-6 text-xs underline" style={{ color: muted }}>{tagalog ? "Punan ang isa pang inquiry" : "Fill out another inquiry"}</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid={`${testidPrefix}-inquiry-form`} className="rounded-xl p-6 sm:p-8 space-y-5" style={{ background: panelBg, border: `1px solid ${border}` }}>
      <div>
        <h3 className="text-2xl font-semibold" style={{ color: "#1A1A1A" }}>{heading}</h3>
        <p className="mt-2 text-sm" style={{ color: muted }}>{subheading}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-name`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Buong Pangalan" : "Full Name"}</label>
          <input id={`${testidPrefix}-inquiry-name`} autoComplete="name" required data-testid={`${testidPrefix}-inquiry-name`} value={form.name} onChange={set("name")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder="Juan Dela Cruz" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-contact`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Telepono o Email" : "Phone or Email"}</label>
          <input id={`${testidPrefix}-inquiry-contact`} autoComplete="email" required data-testid={`${testidPrefix}-inquiry-contact`} value={form.contact} onChange={set("contact")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder="0917 000 0000" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-location`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Lokasyon ng Proyekto" : "Project Location"}</label>
          <input id={`${testidPrefix}-inquiry-location`} autoComplete="address-level2" data-testid={`${testidPrefix}-inquiry-location`} value={form.location} onChange={set("location")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder={tagalog ? "Lungsod / munisipalidad" : "City / municipality"} />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-type`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Uri ng Proyekto" : "Project Type"}</label>
          <select id={`${testidPrefix}-inquiry-type`} data-testid={`${testidPrefix}-inquiry-type`} value={form.projectType} onChange={set("projectType")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none bg-white" style={{ border: `1px solid ${border}` }}>
            <option value="">{tagalog ? "Pumili ng isa" : "Select one"}</option>
            {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-area`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Sukat ng Lote / Sahig" : "Lot / Floor Area"}</label>
          <input id={`${testidPrefix}-inquiry-area`} data-testid={`${testidPrefix}-inquiry-area`} value={form.area} onChange={set("area")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} placeholder={tagalog ? "hal. 120 sqm" : "e.g. 120 sqm"} />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-startdate`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Target na Petsa ng Simula" : "Target Start Date"}</label>
          <input id={`${testidPrefix}-inquiry-startdate`} type="date" data-testid={`${testidPrefix}-inquiry-startdate`} value={form.startDate} onChange={set("startDate")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none" style={{ border: `1px solid ${border}` }} />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-inquiry-budget`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Saklaw ng Badyet" : "Budget Range"}</label>
          <select id={`${testidPrefix}-inquiry-budget`} data-testid={`${testidPrefix}-inquiry-budget`} value={form.budget} onChange={set("budget")} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none bg-white" style={{ border: `1px solid ${border}` }}>
            <option value="">{tagalog ? "Pumili ng isa" : "Select one"}</option>
            {budgetRanges.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-inquiry-details`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Sabihin sa amin ang tungkol sa proyekto" : "Tell us about the project"}</label>
        <textarea id={`${testidPrefix}-inquiry-details`} data-testid={`${testidPrefix}-inquiry-details`} value={form.details} onChange={set("details")} rows={4} className="mt-1.5 w-full rounded-lg px-4 py-2.5 text-sm outline-none resize-none" style={{ border: `1px solid ${border}` }} placeholder={tagalog ? "Mga kuwarto, style, timeline, o anumang makakatulong sa amin na maunawaan ang proyekto" : "Rooms, style, timeline, or anything else that helps us understand the project"} />
      </div>

      <div>
        <label htmlFor={`${testidPrefix}-inquiry-files`} className="text-xs font-medium uppercase tracking-wide" style={{ color: muted }}>{tagalog ? "Mag-upload ng Plano o Larawan (opsyonal)" : "Upload Plans or Photos (optional)"}</label>
        <label htmlFor={`${testidPrefix}-inquiry-files`} data-testid={`${testidPrefix}-inquiry-upload`} className="mt-1.5 flex items-center gap-3 rounded-lg px-4 py-3 text-sm cursor-pointer" style={{ border: `1px dashed ${border}`, color: muted }}>
          <Upload size={16} />
          {fileNames.length ? fileNames.join(", ") : (tagalog ? "Pumili ng files — hindi talaga ina-upload sa konseptong preview na ito" : "Choose files — not actually uploaded in this concept preview")}
          <input id={`${testidPrefix}-inquiry-files`} type="file" multiple className="hidden" onChange={handleFiles} />
        </label>
      </div>

      <button type="submit" data-testid={`${testidPrefix}-inquiry-submit`} className="w-full rounded-lg py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5" style={{ background: accent, color: accentText }}>
        {tagalog ? "Isumite ang Inquiry" : "Submit Inquiry"}
      </button>
      <p className="text-[11px] text-center" style={{ color: muted }}>{tagalog ? "Konseptong preview form lamang ito. Hindi nagpapadala ng tunay na inquiry ang pagsumite nito." : "This is a concept preview form. Submitting it does not send a real inquiry."}</p>
    </form>
  );
}
